// as-cetra-ui/src/components/authenticated/handout/customise-mode/EditableBlock.js

import React, { useState } from 'react';
import { Box } from '@mui/material';
import BlockToolbar from './BlockToolbar';
import EditableParagraph from './EditableParagraph';
import ConfirmRemoveDialog from './ConfirmRemoveDialog';
import { useCustomiseMode } from './CustomiseModeContext';

const EditableBlock = ({
    block,
    sectionIndex,
    blockIndex,
    children, // Original block component (non-editable)
}) => {
    const { isCustomiseMode, editingBlockId, setEditingBlockId, removeBlock, addParagraphBelow } = useCustomiseMode();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const blockId = `${sectionIndex}-${blockIndex}`;
    const isEditing = editingBlockId === blockId;

    // Only paragraphs are editable in Version 2
    const isEditableType = block.type === 'paragraph';

    if (!isCustomiseMode) {
        // Normal rendering
        return children;
    }

    if (!isEditableType) {
        // Non-editable blocks (titles, lists, etc.) - just display normally
        return children;
    }

    // Editable paragraph
    const handleStartEdit = () => {
        setEditingBlockId(blockId);
    };

    const handleFinishEdit = () => {
        setEditingBlockId(null);
    };

    const handleRemove = () => {
        setConfirmOpen(true);
    };

    const handleConfirmRemove = () => {
        removeBlock(sectionIndex, blockIndex);
        setConfirmOpen(false);
    };

    const handleAddBelow = () => {
        addParagraphBelow(sectionIndex, blockIndex);
    };

    return (
        <>
            <Box
                sx={{
                    position: 'relative',
                    border: isCustomiseMode ? '1px dashed rgba(0, 4, 83, 0.2)' : 'none',
                    borderRadius: 1,
                    padding: isCustomiseMode ? 1 : 0,
                    mb: 1,
                    '&:hover': isCustomiseMode
                        ? {
                              backgroundColor: 'rgba(0, 4, 83, 0.02)',
                              '& .block-toolbar': {
                                  opacity: 1,
                              },
                          }
                        : {},
                }}
            >
                <Box
                    className="block-toolbar"
                    sx={{
                        opacity: isEditing ? 1 : 0,
                        transition: 'opacity 0.2s',
                    }}
                >
                    <BlockToolbar
                        onEdit={handleStartEdit}
                        onRemove={handleRemove}
                        onAddBelow={handleAddBelow}
                        isEditing={isEditing}
                    />
                </Box>

                <EditableParagraph
                    block={block}
                    sectionIndex={sectionIndex}
                    blockIndex={blockIndex}
                    isEditing={isEditing}
                    onStartEdit={handleStartEdit}
                    onFinishEdit={handleFinishEdit}
                />
            </Box>

            <ConfirmRemoveDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirmRemove}
                title="Remove this item?"
                message="This cannot be undone unless you exit without saving."
            />
        </>
    );
};

export default EditableBlock;
